import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Leaf, Check, Loader2, Sprout, Sun, Home, Warehouse, Heart, Feather, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = "";

const RegisterGrower = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    city: '',
    description: '',
    grow_type: '',
    specialties: '',
    website: '',
    instagram: '',
    facebook: '',
    image_url: '',
    years_experience: '',
    accepts_wholesale: false,
    offers_clones: false,
    offers_seeds: false,
    women_owned: false,
    native_owned: false,
    bipoc_owned: false
  });

  const growTypes = [
    { id: 'indoor', label: 'Indoor', icon: Home, desc: 'Climate-controlled indoor grow' },
    { id: 'outdoor', label: 'Outdoor', icon: Sun, desc: 'Natural outdoor cultivation' },
    { id: 'greenhouse', label: 'Greenhouse', icon: Warehouse, desc: 'Greenhouse growing' },
    { id: 'mixed', label: 'Mixed', icon: Sprout, desc: 'Combination of methods' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.owner_name || !formData.email || !formData.phone || !formData.city || !formData.description || !formData.grow_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      
      await axios.post(`${API}/api/growers/register`, submitData);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-green-950/10 to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md w-full"
        >
          <Card className="bg-card/80 backdrop-blur border-green-500/20">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for registering as a grower. Our team will review your application and get back to you within 2-3 business days.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/canna-club')} className="w-full bg-green-600 hover:bg-green-700">
                  <Leaf className="mr-2" size={16} />
                  Back to Canna-Club
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                  Go to Homepage
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-green-950/10 to-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/canna-club')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Sprout className="text-green-500" size={20} />
              Grower Registration
            </h1>
            <p className="text-xs text-muted-foreground">Join the Canna-Club grower network</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {/* Info Banner */}
          <Card className="mb-6 bg-green-500/10 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Leaf className="text-green-500 shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-green-400">21+ Only</h3>
                  <p className="text-sm text-muted-foreground">
                    Registration is for licensed cannabis cultivators in Minnesota. All submissions are reviewed by our team.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Info */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Business Information</CardTitle>
                <CardDescription>Tell us about your growing operation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      placeholder="Your grow operation name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      placeholder="Full name"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Minneapolis"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="years_experience">Years Experience</Label>
                    <Input
                      id="years_experience"
                      name="years_experience"
                      type="number"
                      min="0"
                      value={formData.years_experience}
                      onChange={handleChange}
                      placeholder="5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Grow Type */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Grow Type *</CardTitle>
                <CardDescription>Select your primary cultivation method</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {growTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => setFormData(prev => ({ ...prev, grow_type: type.id }))}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.grow_type === type.id
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-border hover:border-green-500/50'
                      }`}
                    >
                      <type.icon className={`mb-2 ${formData.grow_type === type.id ? 'text-green-500' : 'text-muted-foreground'}`} size={24} />
                      <h4 className="font-medium text-sm">{type.label}</h4>
                      <p className="text-xs text-muted-foreground">{type.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Services Offered</CardTitle>
                <CardDescription>What do you offer?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.accepts_wholesale}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accepts_wholesale: checked }))}
                    />
                    <span className="text-sm">Wholesale</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.offers_clones}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, offers_clones: checked }))}
                    />
                    <span className="text-sm">Clones</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.offers_seeds}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, offers_seeds: checked }))}
                    />
                    <span className="text-sm">Seeds</span>
                  </label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialties">Specialties</Label>
                  <Input
                    id="specialties"
                    name="specialties"
                    value={formData.specialties}
                    onChange={handleChange}
                    placeholder="e.g., Organic, Indica strains, CBD-rich"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Ownership & Identity */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Business Ownership</CardTitle>
                <CardDescription>Help us highlight diverse businesses (optional)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-pink-500/5 hover:border-pink-500/30 transition-colors">
                    <Checkbox
                      checked={formData.women_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, women_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Heart size={16} className="text-pink-400" />
                      <span className="text-sm">Women Owned</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-orange-500/5 hover:border-orange-500/30 transition-colors">
                    <Checkbox
                      checked={formData.native_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, native_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Feather size={16} className="text-orange-400" />
                      <span className="text-sm">Native Owned</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-amber-500/5 hover:border-amber-500/30 transition-colors">
                    <Checkbox
                      checked={formData.bipoc_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bipoc_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-amber-400" />
                      <span className="text-sm">BIPOC Owned</span>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">About Your Operation *</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your growing philosophy, experience, and what makes your operation unique..."
                  rows={4}
                  required
                />
              </CardContent>
            </Card>

            {/* Social & Web */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Online Presence</CardTitle>
                <CardDescription>Optional but recommended</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      placeholder="@yourusername"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input
                      id="facebook"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleChange}
                      placeholder="facebook.com/yourpage"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image_url">Profile Image URL</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/your-image.jpg"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  Submitting...
                </>
              ) : (
                <>
                  <Sprout className="mr-2" size={20} />
                  Submit Registration
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting, you confirm you are 21+ and agree to our terms of service.
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterGrower;
